import React, { useEffect, useState, useMemo, useCallback } from "react";
import StandAloneVideoPlayer from "./video/StandAloneVideoPlayer";
import { getSessionPlaylist } from "redux/actions";
import { useDispatch } from "react-redux";
import OBCSpinner from "components/util/OBC";
import StandAloneImagePlayer from "./image/StandAloneImagePlayer";
import _ from "lodash";
import { Button, Slider, Tooltip, Select } from "antd";
import { getSourceList } from "components/OlePatrols/olePatrolsUtils";
import { MEMOIZED_DOMAIN_URL } from "components/util/HostUtils";
import { distanceToRouteCoordinate, getCoordsAtTimestamp } from "components/util/GeometryUtilsV2";
import { getPositionV2, videoFrameExtractor } from "components/util/PlaylistUtils";

const BOUNDS_FOR_EXPLORING_VIDEO = 5;

export const createPlaylistUrls = (currentSession, sourceIndex) => {
    const deviceKey = _.get(currentSession, ["device_uuid"]);
    const sessionKey = _.get(currentSession, ["uuid"]);
    const lastVidUpload = _.get(currentSession, ["last_vid_up"]);

    const rawURL = `https://raw${MEMOIZED_DOMAIN_URL}/${deviceKey}/${sessionKey}/index.m3u8?ts=${lastVidUpload}`;
    const enhancedURL = `https://raw${MEMOIZED_DOMAIN_URL}/${deviceKey}/${sessionKey}/index.m3u8?ts=${lastVidUpload}`;
    const baseURL = `https://raw${MEMOIZED_DOMAIN_URL}/${deviceKey}/${sessionKey}/`;
    const lowResURL = `https://lr${MEMOIZED_DOMAIN_URL}/${deviceKey}/${sessionKey}/index.${sourceIndex}.m3u8?ts=${lastVidUpload}`;

    return {
        raw: rawURL,
        enhanced: enhancedURL,
        base: baseURL,
        lowRes: lowResURL,
    };
};

const ButtonGroup = Button.Group;
const CONTROL_BUTTONS_SIZE = "small";

function getBearing(from, to) {
    const toRad = (deg) => (deg * Math.PI) / 180;
    const toDeg = (rad) => (rad * 180) / Math.PI;

    const lat1 = toRad(from[1]);
    const lon1 = toRad(from[0]);
    const lat2 = toRad(to[1]);
    const lon2 = toRad(to[0]);

    const dLon = lon2 - lon1;

    const y = Math.sin(dLon) * Math.cos(lat2);
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);

    let bearing = toDeg(Math.atan2(y, x));

    return (bearing + 360) % 360; // normalize to 0–360
}

const StandAloneVideoOrImageComponent = ({ selectedSession, positionFilter = {}, startPosition = null, onPositionChanged = () => {} }) => {
    const dispatch = useDispatch();

    const [currentVideoSourceIndex, setCurrentVideoSourceIndex] = useState(0);
    const [playlist, setPlaylist] = useState(null);
    const [playlistUrls, setPlaylistUrls] = useState(null);
    const [index, setIndex] = useState(0);
    const [subIndex, setSubIndex] = useState(1);
    const [isStillsMode, setStillsMode] = useState(false);

    const video = useMemo(() => {
        return _.get(playlist, ["video_playlists", currentVideoSourceIndex], []);
    }, [currentVideoSourceIndex, playlist]);

    // on session change return back to stills mode
    useEffect(() => {
        setStillsMode(false);
    }, [selectedSession]);

    const sources = useMemo(() => {
        const _playlist = {
            image: _.get(playlist, ["image_playlist"], []),
            video: _.get(playlist, ["video_playlists"], []),
        };

        const _streamInfo = _.get(selectedSession, ["stream_info"], null);

        if (!playlist || !_streamInfo) return [];

        const sourceList = getSourceList(_playlist, _streamInfo, selectedSession, true);

        return sourceList;
    }, [playlist, selectedSession]);

    useEffect(() => {
        if (sources.length === 1) {
            const sourceIndex = _.get(sources, [0, "streams", 0, "videoIndex"], 0);
            setCurrentVideoSourceIndex(sourceIndex);
        }
    }, [sources, setCurrentVideoSourceIndex]);

    const framesPerSecond = useMemo(() => {
        return _.get(video, [0, 4], 30);
    }, [video]);

    const currentPlayerPosition = useMemo(() => {
        const result = index + (subIndex - 1) / framesPerSecond;
        return result;
    }, [index, subIndex, framesPerSecond]);

    // update position lat lon on map component when player position changes
    useEffect(() => {
        if (!playlist || !video) return;

        let upcomingVideoTimestamps = [];
        let bearing = null;
        // let timeOffset = 0;

        const routeCoordData2 = _.get(playlist, ["route_locations_v2"], {});
        const distToCoordinates = _.get(playlist, ["route_locations_v2", "distance_to_coordinate"], []);

        // get coordinates for current and next timestamp to calculate bearing of the movement,
        // which will be used to rotate the position marker on the map in the right direction
        _.forEach([0, 1], (i) => {
            const timestamp = _.get(video, [parseInt(currentPlayerPosition) + i, 3, 0], null);
            const coords = getCoordsAtTimestamp(timestamp, routeCoordData2);
            if (coords) {
                upcomingVideoTimestamps.push(coords);
            }
        });

        if (upcomingVideoTimestamps.length === 2) {
            const from = upcomingVideoTimestamps[0];
            const to = upcomingVideoTimestamps[1];
            bearing = getBearing(from, to);
        }

        let ratio = 0;
        let timeOffset = 0;
        const targetKey = video[parseInt(currentPlayerPosition)];

        if (!targetKey) {
            return;
        }

        const frameCount = videoFrameExtractor(targetKey);
        const duration = targetKey[2];

        if (frameCount) {
            ratio = (subIndex - 1) / frameCount;
            if (ratio >= 0) {
                timeOffset = ratio * duration;
            }
        }

        let currentTime = targetKey[1] + timeOffset;

        const position = getPositionV2(currentTime, video, routeCoordData2, 0, true);
        const playlistDistance = position[2];

        if (!playlistDistance) {
            return;
        }

        const coordinates = distanceToRouteCoordinate(playlistDistance, distToCoordinates);

        if (coordinates) {
            onPositionChanged({
                lat: coordinates[1],
                lng: coordinates[0],
                color: "#1890FF",
                bearing: bearing,
            });
        }
    }, [playlist, video, currentPlayerPosition, onPositionChanged, subIndex]);

    useEffect(() => {
        const startPositionValue = _.get(startPosition, ["position"], null);
        const elrValue = _.get(positionFilter, ["elr"], null);

        if (startPositionValue === null || elrValue === null) {
            return;
        }

        if (selectedSession && selectedSession.id) {
            // reset index and subIndex when session changes
            setIndex(0);
            setSubIndex(1);

            const sourceIndex = 0;
            const urls = createPlaylistUrls(selectedSession, sourceIndex);
            setPlaylistUrls(urls);

            let _positionFilter = {
                elr: String(positionFilter.elr).toUpperCase(),
                start_pos: parseInt(startPositionValue) - BOUNDS_FOR_EXPLORING_VIDEO,
                end_pos: parseInt(startPositionValue) + BOUNDS_FOR_EXPLORING_VIDEO,
            };
            dispatch(getSessionPlaylist(selectedSession.id, _positionFilter))
                .then((playlist) => {
                    setPlaylist(playlist);
                })
                .catch((error) => {
                    setPlaylist(null);
                    setPlaylistUrls(null);
                    console.log("Error loading session playlist: ", error);
                });
        } else {
            setPlaylist(null);
            setPlaylistUrls(null);
        }
    }, [dispatch, positionFilter, selectedSession, startPosition]);

    const handleVideoPositionChange = useCallback(
        (newPlayerPosition) => {
            const a1 = parseInt(newPlayerPosition);
            let a2 = (newPlayerPosition - a1) * framesPerSecond;
            a2 = parseInt(a2) + 1;
            setIndex(a1);
            setSubIndex(a2);
        },
        [framesPerSecond],
    );

    const updateCurrentPlayerPosition = useCallback(
        (newPosition) => {
            const a1 = parseInt(newPosition);
            let a2 = (newPosition - a1) * framesPerSecond;
            a2 = parseInt(a2) + 1;
            setIndex(a1);
            setSubIndex(a2);
        },
        [framesPerSecond],
    );

    const videoSelectorButton = useMemo(() => {
        if (sources.length === 1) {
            return null;
        }

        return (
            <Select
                size={CONTROL_BUTTONS_SIZE}
                dropdownMatchSelectWidth={false}
                value={currentVideoSourceIndex}>
                {_.map(sources, (source) => {
                    return _.map(source.streams, (stream) => {
                        const sourceIndex = _.get(stream, "videoIndex", 0);
                        const key = `${sourceIndex}-${stream.name}`;

                        return (
                            <Select.Option
                                key={key}
                                value={sourceIndex}
                                onClick={() => {
                                    setCurrentVideoSourceIndex(sourceIndex);
                                }}>
                                {source.name}
                            </Select.Option>
                        );
                    });
                })}
            </Select>
        );
    }, [sources, currentVideoSourceIndex]);

    const videoOrImageControlsComponent = useMemo(() => {
        return (
            <div className="OlePatrolsExploringVideoOrImagePlayerControls">
                <div className="OlePatrolsExploringPlayerSlider">
                    {isStillsMode && (
                        <Slider
                            min={0}
                            max={video.length}
                            onChange={(value) => {
                                updateCurrentPlayerPosition(value);
                            }}
                            value={currentPlayerPosition}
                            tipFormatter={null}
                            included={false}
                            step={0.01}
                        />
                    )}
                </div>

                <ButtonGroup>
                    {/* Still images */}
                    <Tooltip
                        title="Still Images"
                        arrowPointAtCenter={true}
                        placement="topLeft">
                        <Button
                            type={isStillsMode ? "primary" : "default"}
                            icon="camera"
                            size={CONTROL_BUTTONS_SIZE}
                            onClick={() => {
                                setStillsMode(true);
                            }}
                        />
                    </Tooltip>

                    {/* video selector button */}
                    <Tooltip
                        title="Video"
                        arrowPointAtCenter={true}
                        placement="topLeft">
                        <Button
                            type={!isStillsMode ? "primary" : "default"}
                            icon="video-camera"
                            size={CONTROL_BUTTONS_SIZE}
                            onClick={() => {
                                setStillsMode(false);
                            }}
                        />
                    </Tooltip>
                </ButtonGroup>

                {videoSelectorButton}
            </div>
        );
    }, [isStillsMode, video, currentPlayerPosition, videoSelectorButton, updateCurrentPlayerPosition]);

    const videoOrImagePlayer = useMemo(() => {
        return (
            <>
                {isStillsMode ? (
                    <StandAloneImagePlayer
                        playlist={playlist}
                        playlistUrls={playlistUrls}
                        sourceIndex={currentVideoSourceIndex}
                        setIndex={setIndex}
                        index={index}
                        subIndex={subIndex}
                        setSubIndex={setSubIndex}
                    />
                ) : (
                    <StandAloneVideoPlayer
                        selectedSession={selectedSession}
                        playlist={playlist}
                        sourceIndex={currentVideoSourceIndex}
                        currentPlayerPosition={currentPlayerPosition}
                        setVideoTimestamp={handleVideoPositionChange}
                    />
                )}
                {videoOrImageControlsComponent}
            </>
        );
    }, [
        isStillsMode,
        playlist,
        playlistUrls,
        index,
        subIndex,
        selectedSession,
        currentVideoSourceIndex,
        currentPlayerPosition,
        handleVideoPositionChange,
        videoOrImageControlsComponent,
    ]);

    if (!selectedSession) {
        return (
            <div className="StandAloneVideoOrImageComponent">
                <div>Select a session from the list below</div>
            </div>
        );
    }

    return (
        <div className={`StandAloneVideoOrImageComponent ${playlist ? "Active" : ""}`}>
            {playlist ? (
                videoOrImagePlayer
            ) : (
                <div className="StandAloneVideoOrImageComponentLoading">
                    <div>
                        <OBCSpinner
                            size={70}
                            speed={3}
                            color={"#e8dfff"}
                        />
                    </div>
                    <div className="StandAloneVideoOrImageComponentLoadingText">Loading...</div>
                </div>
            )}
        </div>
    );
};

export default StandAloneVideoOrImageComponent;
